import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  createAiTokenPurchase,
  getAiTokenPrices,
  getAiTokenPurchaseHistory,
  getPublicKeyStripe,
  getUserTokens,
} from "../../HTTP/payment";
import { getSerperQuota } from "../../HTTP/search";

const getErrorMessage = (error, fallback) => error?.message || fallback;

const getList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.prices)) return response.prices;
  if (Array.isArray(response?.purchases)) return response.purchases;
  if (Array.isArray(response?.rows)) return response.rows;
  return [];
};

const getPriceLabel = (price) => price?.title || price?.name || `Offre ${price?.id}`;

const getTokenAmount = (price) =>
  price?.tokenAmount ?? price?.token_amount ?? price?.tokens ?? price?.quantity ?? 0;

const getSerperAmount = (price) => Number(price?.serperAmount ?? price?.serper_amount ?? 0) || 0;

const describePack = (tokens, searches) => {
  const parts = [];
  if (Number(tokens) > 0) parts.push(`${formatTokens(tokens)} tokens`);
  if (Number(searches) > 0) parts.push(`${formatTokens(searches)} recherches`);
  return parts.length ? parts.join(" + ") : "-";
};

const getDisplayPrice = (price) => {
  const currency = String(price?.currency || "EUR").toUpperCase();
  const rawAmount = price?.display_price ?? price?.price ?? price?.unit_amount ?? price?.amount;
  const amount = Number(rawAmount);
  if (!Number.isFinite(amount)) return `${rawAmount ?? "-"} ${currency}`;
  const normalizedAmount = price?.unit_amount !== undefined || price?.price_cents !== undefined
    ? amount >= 100 ? amount / 100 : amount
    : amount;
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(normalizedAmount);
  } catch (error) {
    return `${normalizedAmount.toFixed(2)} ${currency}`;
  }
};

const formatTokens = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? new Intl.NumberFormat("fr-FR").format(amount) : value;
};

function AiTokenPaymentForm({ clientSecret, disabled, onStart, onSuccess, onFailure }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || disabled) return;

    onStart();
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onFailure(submitError.message || "Le formulaire de paiement est invalide.");
        return;
      }

      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/ai-tokens`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        onFailure(result.error.message || "Le paiement a échoué.");
        return;
      }

      onSuccess();
    } catch (error) {
      onFailure(getErrorMessage(error, "Erreur pendant le paiement."));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-5 space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={disabled || !stripe || !elements}
        className="w-full rounded-lg bg-[#00739d] px-4 py-3 font-semibold text-white hover:bg-[#005d7f] disabled:bg-gray-400"
      >
        {disabled ? "Paiement en cours..." : "Confirmer le paiement"}
      </button>
    </form>
  );
}

function AiTokenPayment({ clientSecret, stripeKey, disabled, onStart, onSuccess, onFailure }) {
  const stripePromise = useMemo(() => (stripeKey ? loadStripe(stripeKey) : null), [stripeKey]);
  if (!stripePromise || !clientSecret) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: "stripe", variables: { colorPrimary: "#00739d" } },
      }}
    >
      <AiTokenPaymentForm
        clientSecret={clientSecret}
        disabled={disabled}
        onStart={onStart}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
    </Elements>
  );
}

export default function AiTokenPurchase() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch (error) {
      return null;
    }
  }, []);

  const [prices, setPrices] = useState([]);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [history, setHistory] = useState([]);
  const [tokens, setTokens] = useState(null);
  const [searchQuota, setSearchQuota] = useState(null);
  const [stripeKey, setStripeKey] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const refreshAccount = useCallback(async () => {
    if (!user?.id) return;
    const [tokensResponse, historyResponse, quotaResponse] = await Promise.all([
      getUserTokens(user.id),
      getAiTokenPurchaseHistory(user.id),
      getSerperQuota().catch(() => null),
    ]);
    setTokens(tokensResponse?.tokens ?? null);
    setSearchQuota(quotaResponse);
    setHistory(getList(historyResponse));
  }, [user?.id]);

  useEffect(() => {
    const loadPage = async () => {
      if (!user?.id) {
        setStatus("error");
        setErrorMessage("Utilisateur introuvable. Merci de vous reconnecter.");
        return;
      }

      try {
        const [pricesResponse, stripeResponse] = await Promise.all([
          getAiTokenPrices("eur"),
          getPublicKeyStripe(),
        ]);
        const availablePrices = getList(pricesResponse);
        setPrices(availablePrices);
        setSelectedPriceId(availablePrices[0]?.id ?? null);
        setStripeKey(stripeResponse?.value || stripeResponse?.publicKey || "");
        await refreshAccount();
        setStatus("ready");
      } catch (error) {
        setStatus("error");
        setErrorMessage(getErrorMessage(error, "Impossible de charger les offres de tokens IA."));
      }
    };

    loadPage();
  }, [refreshAccount, user?.id]);

  const selectedPrice = prices.find((price) => String(price.id) === String(selectedPriceId));
  const isBusy = status === "creating" || status === "processing";

  const createPurchase = async () => {
    if (!selectedPrice || isBusy) return;
    setStatus("creating");
    setErrorMessage("");
    try {
      const purchase = await createAiTokenPurchase(selectedPrice.id);
      if (!purchase?.client_secret) throw new Error("Le secret de paiement est introuvable.");
      setClientSecret(purchase.client_secret);
      setStatus("payment_required");
    } catch (error) {
      setStatus("ready");
      setErrorMessage(getErrorMessage(error, "Impossible de créer le paiement."));
    }
  };

  const handlePaymentSuccess = async () => {
    setErrorMessage("");
    try {
      await refreshAccount();
      setStatus("ready");
      setClientSecret("");
    } catch (error) {
      setStatus("ready");
      setErrorMessage(getErrorMessage(error, "Paiement confirmé, mais impossible de rafraîchir les données."));
    }
  };

  const statusLabel = {
    pending: "En attente",
    paid: "Payé",
    canceled: "Annulé",
  };

  if (status === "loading") {
    return <section className="mx-auto max-w-5xl px-4 py-8">Chargement des offres de tokens IA...</section>;
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Tokens IA et recherches</h1>
        <p className="mt-2 text-gray-600">Achetez des tokens et des recherches Serper supplémentaires.</p>
      </header>

      {errorMessage && (
        <p className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">{errorMessage}</p>
      )}

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Solde actuel</p>
        <p className="mt-2 text-3xl font-bold text-[#00739d]">
          {tokens === null ? "Indisponible" : formatTokens(tokens)}
        </p>
        {searchQuota && (
          <div className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-700">
            <p className="font-semibold uppercase tracking-wide text-gray-500">Recherches Serper</p>
            <p className="mt-1 text-2xl font-bold text-[#00739d]">{formatTokens(searchQuota.remaining ?? 0)} restantes</p>
            <p className="mt-1">
              Quota mensuel : {formatTokens(searchQuota.used ?? 0)}/{formatTokens(searchQuota.limit ?? 0)} utilisées
              {" · "}Solde acheté (sans expiration) : {formatTokens(searchQuota.credits ?? 0)}
            </p>
          </div>
        )}
      </div>

      <div className="h-[300px] overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900">Choisir une offre</h2>
        {prices.length === 0 ? (
          <p className="mt-4 text-gray-600">Aucune offre disponible.</p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {prices.map((price) => {
              const selected = String(price.id) === String(selectedPriceId);
              return (
                <button
                  key={price.id}
                  type="button"
                  onClick={() => setSelectedPriceId(price.id)}
                  disabled={isBusy || status === "payment_required"}
                  className={`rounded-lg border p-4 text-left ${selected ? "border-[#00739d] bg-cyan-50" : "border-gray-200 hover:border-[#00739d]"}`}
                >
                  <p className="font-semibold text-gray-900">{getPriceLabel(price)}</p>
                  <p className="mt-2 text-sm text-gray-600">{describePack(getTokenAmount(price), getSerperAmount(price))}</p>
                  <p className="mt-3 text-xl font-bold text-gray-900">{getDisplayPrice(price)}</p>
                </button>
              );
            })}
          </div>
        )}

        <button
          type="button"
          onClick={createPurchase}
          disabled={!selectedPrice || isBusy || status === "payment_required"}
          className="mt-5 rounded-lg bg-[#00739d] px-5 py-3 font-semibold text-white hover:bg-[#005d7f] disabled:bg-gray-400"
        >
          {status === "creating" ? "Création du paiement..." : "Acheter"}
        </button>

        {(status === "payment_required" || status === "processing") && (
          <div className="mt-6 border-t border-gray-200 pt-5">
            <h3 className="text-lg font-semibold text-gray-900">Paiement sécurisé</h3>
            <AiTokenPayment
              clientSecret={clientSecret}
              stripeKey={stripeKey}
              disabled={status === "processing"}
              onStart={() => setStatus("processing")}
              onSuccess={handlePaymentSuccess}
              onFailure={(message) => {
                setStatus("ready");
                setErrorMessage(message);
              }}
            />
          </div>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-gray-900">Historique des achats</h2>
        {history.length === 0 ? (
          <p className="mt-4 text-gray-600">Aucun achat de tokens IA.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="border-b border-gray-200 text-gray-500">
                <tr><th className="px-3 py-2">Créé le</th><th className="px-3 py-2">Contenu</th><th className="px-3 py-2">Montant</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Payé le</th></tr>
              </thead>
              <tbody>
                {history.map((purchase) => (
                  <tr key={purchase.id} className="border-b border-gray-100">
                    <td className="px-3 py-3">{purchase.created_at ? new Date(purchase.created_at).toLocaleString("fr-FR") : "-"}</td>
                    <td className="px-3 py-3">{describePack(purchase.token_amount, purchase.serper_amount)}</td>
                    <td className="px-3 py-3">{purchase.amount ?? "-"} {String(purchase.currency || "").toUpperCase()}</td>
                    <td className="px-3 py-3 font-semibold">{statusLabel[purchase.status] || purchase.status || "-"}</td>
                    <td className="px-3 py-3">{purchase.paid_at ? new Date(purchase.paid_at).toLocaleString("fr-FR") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
